import { useEffect, useRef, useState } from "react";

/**
 * Count-up animation from 0 to `target` on mount and whenever `target` changes.
 * Uses requestAnimationFrame with an ease-out cubic curve; no dependencies.
 * Respects prefers-reduced-motion (jumps to final value).
 */
export function useCountUp(target: number, durationMs = 900): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }

    const start = performance.now();
    const from = 0;
    const to = target;

    function step(now: number) {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    }
    rafRef.current = requestAnimationFrame(step);

    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs]);

  return value;
}
